import React, { useState } from 'react';
import { Card, Button, Space, Typography, Row, Col, message, Progress, Statistic, Alert, Table, Tag, Select } from 'antd';
import { PlayCircleOutlined, PauseCircleOutlined, ReloadOutlined, UserOutlined, PictureOutlined, VideoCameraOutlined, InstagramOutlined } from '@ant-design/icons';
import { useScrapingStatus, useTriggerSpecificScrape, useTriggerProfileScrape } from './api';

const { Title, Text } = Typography;
const { Option } = Select;

interface ScrapingMetrics {
  creatorsProcessed: number;
  contentScraped: number;
  contentFiltered: number;
  contentSaved: number;
  startTime: string;
  endTime?: string;
  duration: number;
  errors: number;
  content_type: string;
  apiCallsMade: number;
}

interface ScrapingStatus {
  isRunning: boolean;
  currentOperation?: string;
  progress?: number;
  lastRun?: string;
  metrics?: ScrapingMetrics;
}

const ScrapingTab: React.FC = () => {
  const [selectedContentType, setSelectedContentType] = useState<string>('all');

  // Use the new API hooks
  const { data: scrapingStatusResponse, isLoading, refetch } = useScrapingStatus();
  const startScrapingMutation = useTriggerSpecificScrape();
  const startProfileScrapingMutation = useTriggerProfileScrape();

  const scrapingStatus = scrapingStatusResponse?.data;

  const contentTypeOptions = [
    { value: 'all', label: 'All Content Types', icon: <InstagramOutlined />, color: 'purple' },
    { value: 'stories', label: 'Stories Only', icon: <UserOutlined />, color: 'blue' },
    { value: 'posts', label: 'Posts Only', icon: <PictureOutlined />, color: 'green' },
    { value: 'reels', label: 'Reels Only', icon: <VideoCameraOutlined />, color: 'orange' },
  ];

  const getContentTypeInfo = (type: string) => {
    return contentTypeOptions.find(opt => opt.value === type) || contentTypeOptions[0];
  };

  const formatDuration = (milliseconds: number) => {
    const seconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    
    if (hours > 0) return `${hours}h ${minutes % 60}m ${seconds % 60}s`;
    if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
    return `${seconds}s`;
  };

  const calculateEfficiency = (metrics?: ScrapingMetrics) => {
    if (!metrics || metrics.contentScraped === 0) return 0;
    return Math.round((metrics.contentSaved / metrics.contentScraped) * 100);
  };

  const recentScrapingHistory = [
    {
      key: '1',
      timestamp: '2024-06-23 14:30:00',
      content_type: 'All',
      duration: '2m 34s',
      scraped: 45,
      saved: 12,
      efficiency: '27%',
      status: 'Completed',
    },
    {
      key: '2',
      timestamp: '2024-06-23 13:15:00',
      content_type: 'Stories',
      duration: '45s',
      scraped: 18,
      saved: 3,
      efficiency: '17%',
      status: 'Completed',
    },
    {
      key: '3',
      timestamp: '2024-06-23 12:00:00',
      content_type: 'Posts',
      duration: '1m 12s',
      scraped: 22,
      saved: 8,
      efficiency: '36%',
      status: 'Completed',
    },
  ];

  const historyColumns = [
    {
      title: 'Timestamp',
      dataIndex: 'timestamp',
      key: 'timestamp',
      render: (text: string) => <Text type="secondary">{text}</Text>,
    },
    {
      title: 'Content Type',
      dataIndex: 'content_type',
      key: 'content_type',
      render: (type: string) => {
        const info = getContentTypeInfo(type.toLowerCase());
        return <Tag color={info.color}>{info.icon} {type}</Tag>;
      },
    },
    {
      title: 'Duration',
      dataIndex: 'duration',
      key: 'duration',
    },
    {
      title: 'Scraped',
      dataIndex: 'scraped',
      key: 'scraped',
    },
    {
      title: 'Saved',
      dataIndex: 'saved',
      key: 'saved',
    },
    {
      title: 'Efficiency',
      dataIndex: 'efficiency',
      key: 'efficiency',
      render: (efficiency: string) => {
        const value = parseInt(efficiency);
        const color = value > 30 ? 'success' : value > 15 ? 'warning' : 'error';
        return <Tag color={color}>{efficiency}</Tag>;
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={status === 'Completed' ? 'success' : 'processing'}>{status}</Tag>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={3}>
          <PlayCircleOutlined /> Scraping Control Center
        </Title>
        <Button 
          icon={<ReloadOutlined />} 
          onClick={() => refetch()}
          loading={isLoading}
        >
          Refresh Status
        </Button>
      </div>

      {/* Current Status Alert */}
      {scrapingStatus?.isRunning && (
        <Alert
          message="Scraping in Progress"
          description={`Current operation: ${scrapingStatus.currentOperation || 'Processing content...'}`}
          type="info"
          showIcon
          style={{ marginBottom: '24px' }}
          action={
            <Progress 
              percent={scrapingStatus.progress || 0} 
              size="small" 
              style={{ width: 200 }}
            />
          }
        />
      )}

      {/* Scraping Controls */}
      <Card title="Start New Scraping Operation" style={{ marginBottom: '24px' }}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <div>
            <Text strong>Select Content Type:</Text>
            <Select
              value={selectedContentType}
              onChange={setSelectedContentType}
              style={{ width: 300, marginLeft: 8 }}
              disabled={scrapingStatus?.isRunning}
            >
              {contentTypeOptions.map(option => (
                <Option key={option.value} value={option.value}>
                  {option.icon} {option.label}
                </Option>
              ))}
            </Select>
          </div>
          
          <Space>
            <Button
              type="primary"
              icon={<PlayCircleOutlined />}
              onClick={() => startScrapingMutation.mutate(selectedContentType)}
              loading={startScrapingMutation.isLoading}
              disabled={scrapingStatus?.isRunning}
              size="large"
            >
              Start Scraping
            </Button>
            
            {scrapingStatus?.isRunning && (
              <Button
                icon={<PauseCircleOutlined />}
                disabled // TODO: Implement stop functionality
              >
                Stop Scraping
              </Button>
            )}
          </Space>

          <Text type="secondary">
            Estimated duration: {selectedContentType === 'all' ? '2-5 minutes' : '30-90 seconds'} depending on content volume
          </Text>
        </Space>
      </Card>

      {/* Profile Scraping Section */}
      <Card title="Tracked Creators Profile Update" style={{ marginBottom: '24px' }}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Text>
            Update profile information (follower count, bio, display name, etc.) for all tracked creators.
          </Text>
          
          <Space>
            <Button
              type="default"
              icon={<UserOutlined />}
              onClick={() => startProfileScrapingMutation.mutate()}
              loading={startProfileScrapingMutation.isLoading}
              disabled={scrapingStatus?.isRunning}
              size="large"
            >
              Update Creator Profiles
            </Button>
          </Space>

          <Text type="secondary">
            Estimated duration: 30-60 seconds for all tracked creators
          </Text>
        </Space>
      </Card>

      {/* Current Operation Metrics */}
      {scrapingStatus?.metrics && (
        <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Creators Processed"
                value={scrapingStatus.metrics.creatorsProcessed}
                prefix={<UserOutlined />}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Content Scraped"
                value={scrapingStatus.metrics.contentScraped}
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Content Saved"
                value={scrapingStatus.metrics.contentSaved}
                suffix={`/ ${scrapingStatus.metrics.contentScraped}`}
                valueStyle={{ color: '#faad14' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Efficiency"
                value={calculateEfficiency(scrapingStatus.metrics)}
                suffix="%"
                valueStyle={{ 
                  color: calculateEfficiency(scrapingStatus.metrics) > 30 ? '#52c41a' : '#faad14' 
                }}
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* Additional Metrics */}
      {scrapingStatus?.metrics && (
        <Row gutter={16} style={{ marginBottom: '24px' }}>
          <Col xs={24} md={12}>
            <Card title="Operation Details">
              <Space direction="vertical" style={{ width: '100%' }}>
                <div>
                  <Text strong>Content Type:</Text> {scrapingStatus.metrics.content_type}
                </div>
                <div>
                  <Text strong>Duration:</Text> {formatDuration(scrapingStatus.metrics.duration)}
                </div>
                <div>
                  <Text strong>API Calls Made:</Text> {scrapingStatus.metrics.apiCallsMade}
                </div>
                <div>
                  <Text strong>Errors:</Text> 
                  <Tag color={scrapingStatus.metrics.errors > 0 ? 'error' : 'success'}>
                    {scrapingStatus.metrics.errors}
                  </Tag>
                </div>
              </Space>
            </Card>
          </Col>
          <Col xs={24} md={12}>
            <Card title="Filter Performance">
              <Space direction="vertical" style={{ width: '100%' }}>
                <div>
                  <Text strong>Total Processed:</Text> {scrapingStatus.metrics.contentScraped}
                </div>
                <div>
                  <Text strong>Matched Filter:</Text> {scrapingStatus.metrics.contentSaved}
                </div>
                <div>
                  <Text strong>Filtered Out:</Text> {scrapingStatus.metrics.contentFiltered}
                </div>
                <div>
                  <Text strong>Filter Rate:</Text> 
                  <Tag color="blue">
                    {scrapingStatus.metrics.contentScraped > 0 
                      ? Math.round((scrapingStatus.metrics.contentFiltered / scrapingStatus.metrics.contentScraped) * 100)
                      : 0}%
                  </Tag>
                </div>
              </Space>
            </Card>
          </Col>
        </Row>
      )}

      {/* Recent Scraping History */}
      {/*<Card title="Recent Scraping History">*/}
      {/*  <Table*/}
      {/*    dataSource={recentScrapingHistory}*/}
      {/*    columns={historyColumns}*/}
      {/*    pagination={false}*/}
      {/*    size="small"*/}
      {/*  />*/}
      {/*</Card>*/}

      {/* Scraping Information */}
      <Card title="Scraping Information" style={{ marginTop: '24px' }}>
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <div>
                <Text strong>Content Types:</Text>
                <ul>
                  <li><Text>Stories:</Text> Temporary content with hashtags/mentions</li>
                  <li><Text>Posts:</Text> Permanent feed content with captions</li>
                  <li><Text>Reels:</Text> Short-form video content</li>
                </ul>
              </div>
            </Space>
          </Col>
          <Col xs={24} md={12}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <div>
                <Text strong>Content Scraping Process:</Text>
                <ul>
                  <li>Fetches content from tracked creators</li>
                  <li>Applies global filtering rules</li>
                  <li>Saves matching content to database</li>
                  <li>Prevents duplicate processing</li>
                </ul>
              </div>
              <div style={{ marginTop: '16px' }}>
                <Text strong>Profile Update Process:</Text>
                <ul>
                  <li>Updates creator profile information</li>
                  <li>Syncs follower/following counts</li>
                  <li>Updates bio and display name</li>
                  <li>Maintains current profile data</li>
                </ul>
              </div>
            </Space>
          </Col>
        </Row>
      </Card>
    </div>
  );
};

export default ScrapingTab;